import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StyleSheet, css } from 'aphrodite';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress } from '@mui/material';
import {
  convertToInitialFormData,
  showToast,
  toTitleCase
} from '../../../../utils/user/patient/helpers';
import { updatePatientData } from '../../../../redux/middleware/user/patient/patient_data';

// Section component imports
import ProfilePicture from '../profile_sections/ProfilePicture';
import PersonalInformation from '../profile_sections/PersonalInformation';
import ContactInformation from '../profile_sections/ContactInformation';
import EmergencyContact from '../profile_sections/EmergencyContact';
import MedicalInformation from '../profile_sections/MedicalInformation';
import Preferences from '../profile_sections/Preferences';
import InsuranceInformation from '../profile_sections/InsuranceInformation';
// import Summary from '../profile_sections/Summary';
import { TOASTIFY_NEWEST_ON_TOP } from '../../../../utils/user/patient/config';

const sections = [
  {
    id: 'profile',
    label: 'Profile Picture',
    key: 'profilePicture',
    Component: ProfilePicture
  },
  {
    id: 'personal',
    label: 'Personal Information',
    key: 'personalInfo',
    Component: PersonalInformation
  },
  {
    id: 'contact',
    label: 'Contact Information',
    key: 'contactInfo',
    Component: ContactInformation
  },
  {
    id: 'emergency',
    label: 'Emergency Contact',
    key: 'emergencyContact',
    Component: EmergencyContact
  },
  {
    id: 'medical',
    label: 'Medical Information',
    key: 'medicalInfo',
    Component: MedicalInformation
  },
  {
    id: 'preferences',
    label: 'Preferences',
    key: 'preferences',
    Component: Preferences
  },
  {
    id: 'insurance',
    label: 'Insurance Information',
    key: 'insuranceInfo',
    Component: InsuranceInformation
  }
];

const Page = ({ toast }) => {
  const dispatch = useDispatch();

  const { loading, patient: patientData } = useSelector(
    (state) => state.patientData
  );

  // Initialize formData (pre-filled where available)
  // const initialFormData = {
  // 	profilePicture: null,
  // 	personalInfo: { ...preFilledData },
  // 	contactInfo: { ...preFilledData },
  // 	emergencyContact: {},
  // 	medicalInfo: {},
  // 	preferences: {},
  // 	insuranceInfo: {},
  // 	// summary: {},
  // };
  const initialFormData = convertToInitialFormData(patientData);

  const [formData, setFormData] = useState(initialFormData);

  // Track which section is currently open
  const [openSection, setOpenSection] = useState(sections[0].id);

  const handleSectionToggle = (sectionId) => {
    setOpenSection((prev) => (prev === sectionId ? prev : sectionId));
  };

  // Update the formData when a section is completed
  const handleSectionComplete = async (data, sectionId) => {
    const newFormData = {
      ...formData,
      [sections.find((s) => s.id === sectionId).key]: data
    };
    setFormData(newFormData);
    return newFormData;
  };

  // Navigation between sections.
  const moveToPreviousSection = () => {
    const currentIndex = sections.findIndex((s) => s.id === openSection);
    if (currentIndex > 0) {
      setOpenSection(sections[currentIndex - 1].id);
    }
  };

  // Helper functions for flattening data
  const getValue = (obj, key, defaultValue = '') =>
    obj && obj[key] ? obj[key] : defaultValue;
  const getArrayValue = (obj, key) =>
    obj && Array.isArray(obj[key])
      ? obj[key].filter((item) => item && item.trim() !== '')
      : [];

  // Function to clear the form data after successful submission
  const clearFormData = () => {
    setFormData(initialFormData);
  };

  // Final submit builds the flattened data from formData.
  const handleSubmit = async (data, sectionId) => {
    // Update section data in formData
    const newFormData = await handleSectionComplete(data, sectionId);

    try {
      const flattenedData = {
        first_name: toTitleCase(
          getValue(newFormData.personalInfo, 'firstName')
        ),
        last_name: toTitleCase(getValue(newFormData.personalInfo, 'lastName')),
        date_of_birth: getValue(newFormData.personalInfo, 'dateOfBirth'),
        gender: getValue(newFormData.personalInfo, 'gender', '').toUpperCase(),

        phone_number: getValue(newFormData.contactInfo, 'phoneNumber'),
        alternate_phone_number: getValue(
          newFormData.contactInfo,
          'alternatePhoneNumber'
        ),
        country: toTitleCase(getValue(newFormData.contactInfo, 'country')),
        state_province: toTitleCase(getValue(newFormData.contactInfo, 'state')),
        city: toTitleCase(getValue(newFormData.contactInfo, 'city')),
        street_address: toTitleCase(
          getValue(newFormData.contactInfo, 'streetAddress')
        ),
        postal_code: getValue(newFormData.contactInfo, 'postalCode'),

        emergency_contact_name: toTitleCase(
          getValue(newFormData.emergencyContact, 'name')
        ),
        emergency_contact_relationship: getValue(
          newFormData.emergencyContact,
          'relationship'
        ).toUpperCase(),
        emergency_contact_phone: getValue(
          newFormData.emergencyContact,
          'phoneNumber'
        ),
        emergency_contact_email: getValue(
          newFormData.emergencyContact,
          'email'
        ),

        blood_group: getValue(
          newFormData.medicalInfo,
          'bloodGroup'
        ).toUpperCase(),
        known_allergies: getArrayValue(
          newFormData.medicalInfo,
          'allergies'
        ).map(toTitleCase),
        chronic_conditions: getArrayValue(
          newFormData.medicalInfo,
          'chronicConditions'
        ).map(toTitleCase),
        current_medications: getArrayValue(
          newFormData.medicalInfo,
          'currentMedications'
        ).map(toTitleCase),
        primary_physician: toTitleCase(
          getValue(newFormData.medicalInfo, 'primaryPhysician')
        ),

        preferred_language: toTitleCase(
          getValue(newFormData.preferences, 'preferredLanguage')
        ),
        notification_preferences: getValue(
          newFormData.preferences,
          'notificationPreferences'
        ),

        insurance_name: toTitleCase(
          getValue(newFormData.insuranceInfo, 'insuranceProvider')
        ),
        insurance_number: getValue(newFormData.insuranceInfo, 'policyNumber'),
        insurance_expiry: getValue(newFormData.insuranceInfo, 'expiryDate')
      };

      const cleanedData = Object.fromEntries(
        Object.entries(flattenedData).filter(
          ([, value]) =>
            value !== '' &&
						value !== null &&
						value !== undefined &&
						(Array.isArray(value) ? value.length > 0 : true)
        )
      );

      const formDataToSend = new FormData();
      if (newFormData.profilePicture) {
        formDataToSend.append('profile_picture', newFormData.profilePicture);
      }
      formDataToSend.append('data', JSON.stringify(cleanedData));

      const response = await dispatch(updatePatientData(formDataToSend));
      if (response.status === 200) {
        showToast(toast, 'success', 'Profile updated successfully');
        // Clear formData after a successful submission
        clearFormData();
      } else {
        showToast(
          toast,
          'error',
          response.data?.detail || 'Unexpected response from server'
        );
      }
    } catch (error) {
      console.error('Error submitting profile:', error);
      showToast(
        toast,
        'error',
        error.response?.data?.message ||
					'There was an error updating your profile.'
      );
    }
  };

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.contentWrapper)}>
        <h1 className={css(styles.title)}>Update Your Profile</h1>
        {/* Tabs Header */}
        <div className={css(styles.tabsHeader)}>
          {sections.map((section) => (
            <motion.button
              key={section.id}
              className={css(
							  styles.tabItem,
							  openSection === section.id && styles.activeTab
              )}
              onClick={() => setOpenSection(section.id)}
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            >
              {section.label}
            </motion.button>
          ))}
          <div className={css(styles.tabsGradient)} />
        </div>
        {/* Tab Content */}
        <AnimatePresence mode='wait'>
          {openSection &&
						(() => {
						  const ActiveComponent = sections.find(
						    (s) => s.id === openSection
						  ).Component;
						  return (
  <motion.div
    key={openSection}
    className={css(styles.sectionContent)}
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -20 }}
    transition={{ duration: 0.3 }}
  >
    <ActiveComponent
      initialData={
											formData[
											  sections.find((s) => s.id === openSection).key
											] || {}
										}
      onComplete={(data) => {
										  handleSubmit(data, openSection);
      }}
      optional
      onBack={() => moveToPreviousSection()}
    />
  </motion.div>
						  );
						})()}
        </AnimatePresence>
      </div>
    </div>
  );
};

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f0f4f8',
    boxSizing: 'border-box'
  },
  contentWrapper: {
    width: '100%',
    backgroundColor: '#ffffff',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
    padding: '2rem'
  },
  title: {
    fontSize: '1.75rem',
    textAlign: 'center',
    marginBottom: '1.5rem',
    color: '#2783F7',
    fontWeight: 'bold'
  },
  tabsHeader: {
    display: 'flex',
    alignItems: 'center',
    overflowX: 'auto',
    whiteSpace: 'nowrap',
    marginBottom: '1.5rem',
    borderBottom: '1px solid #e2e8f0',
    position: 'relative',
    // Hide scrollbar in WebKit browsers
    WebkitOverflowScrolling: 'touch'
  },
  tabItem: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    fontWeight: '600',
    color: '#4a5568',
    transition: 'color 0.3s, border-bottom 0.3s',
    flexShrink: 0
  },
  activeTab: {
    color: '#2783F7',
    borderBottom: '3px solid #2783F7'
  },
  sectionContent: {
    padding: '1rem',
    overflow: 'hidden'
  },
  navigationButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '1rem'
  },
  navButton: {
    backgroundColor: '#2783F7',
    color: 'white',
    border: 'none',
    padding: '0.5rem 1rem',
    fontSize: '1rem',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    ':hover': {
      backgroundColor: '#1c6ed8'
    }
  },
  submitContainer: {
    textAlign: 'center',
    marginTop: '1.5rem'
  },
  submitButton: {
    backgroundColor: '#2783F7',
    color: '#ffffff',
    border: 'none',
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    fontWeight: '600',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'transform 0.2s, background-color 0.2s',
    ':disabled': {
      backgroundColor: '#a0aec0',
      cursor: 'not-allowed'
    }
  },
  tabsGradient: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: '2rem',
    background: 'linear-gradient(to left, #ffffff, rgba(255,255,255,0))',
    pointerEvents: 'none'
  }
});

export default Page;
